import React, { Fragment, FC, useState, FormEvent } from 'react'
import { Confirmation } from '../shared/Confirmation'
import { Dialog } from 'components/Dialog'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useTranslation } from 'react-i18next'
import { Stack } from 'components/Stack'
import { useMapEditorContext } from 'context/map-editor-context'
import { useParams } from 'react-router-dom'
import { useFetchProject } from 'services/project'
import { Flex } from 'components/Flex'
import { Spinner } from 'components/Spinner'
import styles from './CustomReportWizard.module.scss'
import { PointCoordinates } from 'models/map'
import { coordinatesToLngLat } from 'utils/coordinates'
import { useFetchOrganization } from 'services/organization'
import { useUserContext } from 'context/user-context'
import { StorageProvider } from 'providers/storage'
import { DropDownMenu } from 'components/DropDownMenu'
import { DropDownElement } from 'components/DropDownElement'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { Loading } from 'components/Loading'
import { Icon } from 'components/Icon'
import { Alert } from 'components/Alert'

const getAuthorizationHeaders = (): Record<string, string> => {
  const token = StorageProvider.get('token')

  return token ? { Authorization: `JWT ${token}` } : {}
}

type Steps = 'confirm' | 'download' | 'loading' | 'ready'
export const CustomReportWizard: FC = () => {
  const { t } = useTranslation()
  const { projectId } = useParams()
  const [currentUser] = useUserContext()
  const { status: loadStatus } = useFetchProject(projectId)
  const [{ confirmed, editor }, mapEditorDispatch] = useMapEditorContext()
  const { organization } = useFetchOrganization()
  const coordinates = editor.feature?.geometry?.coordinates as PointCoordinates
  const [step, setStep] = useState<Steps>('confirm')
  const [reportName, setReportName] = useState<string | undefined>(undefined)
  const [orgReports, setOrgReports] = useState<undefined | []>(undefined)
  const [currentReport, setCurrentReport] = useState<undefined | number>(undefined)
  const [noReportName, setNoReportName] = useState<boolean>(false)

  React.useEffect(() => {
    if (organization?.customReport2) {
      const fetchReports = async () => {
        const data = await fetch(
          `https://mapserver.descifra.global/v1/getReportNames/${organization.id}`,
          { headers: getAuthorizationHeaders() },
        )
        const res = await data.json()

        setOrgReports(res)
      }

      fetchReports()
    }
    // eslint-disable-next-line
  }, [])

  const onDialogClose = () => {
    mapEditorDispatch.cancelWizard()
  }

  const handleInputChange = (e: any) => {
    setReportName(e.target.value)
  }

  const handleReportChange = (e: any) => {
    setCurrentReport(e.target.value)
  }

  const onSubmit = (event: FormEvent) => {
    const orgid = organization?.id

    if (!reportName) {
      setNoReportName(true)

      setTimeout(() => {
        setNoReportName(false)
      }, 5000)
    } else {
      const reportRequestData = {
        ...coordinatesToLngLat(coordinates),
        projectId,
        orgid,
      }

      getReportData(reportRequestData)
    }
  }

  const getReportData = async (reportRequestData: any) => {
    setStep('loading')
    const name = reportName
    const reportId = currentReport
    const availableResidences = 0
    const locationUrl = window.location.href
    const urlArr = locationUrl.split('/')
    const projectArrId = urlArr[4]
    const userid = currentUser?.id
    const language = StorageProvider.get('language')

    const customReportUrl =
      'https://report.descifra.global/GetReports.ashx?idorg=' +
      reportRequestData.orgid +
      '&lat=' +
      reportRequestData.lat +
      '&lng=' +
      reportRequestData.lng +
      '&nombre=' +
      name +
      '&Key=RGVzY2lmcmEwOTIz&correo=' +
      String(currentUser!.email) +
      '&type=custom&mr=' +
      String(organization!.mobilityReport) +
      '&pr=' +
      String(organization!.predictiveReport) +
      '&projectid=' +
      String(projectArrId) +
      '&userid=' +
      String(userid) +
      '&totalhog=' +
      String(availableResidences) +
      '&language=' +
      language

    const reportSelectorURL =
      'https://report.descifra.global/GetReports.ashx?idorg=' +
      reportRequestData.orgid +
      '&lat=' +
      reportRequestData.lat +
      '&lng=' +
      reportRequestData.lng +
      '&nombre=' +
      name +
      '&Key=RGVzY2lmcmEwOTIz&correo=' +
      String(currentUser!.email) +
      '&type=custom&mr=' +
      String(organization!.mobilityReport) +
      '&pr=' +
      String(organization!.predictiveReport) +
      '&projectid=' +
      String(projectArrId) +
      '&userid=' +
      String(userid) +
      '&totalhog=' +
      String(availableResidences) +
      '&language=' +
      language +
      '&report_id=' +
      reportId

    if (organization?.customReport2) {
      const data = await fetch(reportSelectorURL, { headers: getAuthorizationHeaders() })
      const res = await data.json()

      if (res[0].status === '200') {
        setStep('ready')
      } else {
        alert('Error en la generación del reporte, contacta a soporte.')
      }
    } else {
      const data = await fetch(customReportUrl, { headers: getAuthorizationHeaders() })
      const res = await data.json()

      if (res[0].status === '200') {
        setStep('ready')
      } else {
        alert('Error en la generación del reporte, contacta a soporte.')
      }
    }
  }

  const customReportDialog = (
    <Dialog
      isOpen={confirmed}
      onClose={onDialogClose}
      aria-label={t(`custom_report_dialog.${step}.title`)}
      className={styles.customReportDialog}
    >
      {loadStatus === 'loading' && (
        <Flex justify="center" align="center">
          <Spinner />
        </Flex>
      )}
      {loadStatus === 'success' && (
        <Stack>
          <div className={styles.icono1}>
            <Icon className={styles.icono} name="markup-line" size="xl" />
            <Heading className={styles.icono2}>{t(`custom_report_dialog.${step}.title`)}</Heading>
          </div>
          {step === 'confirm' && (
            <div>
              <Heading size="widget">{t(`custom_report_dialog.${step}.cta`)}</Heading>
              <br></br>
              <TextInput
                name="reportName"
                label={t(`custom_report_dialog.${step}.placeholder`)}
                value={reportName}
                onChange={handleInputChange}
              />

              {noReportName && (
                <Fragment>
                  <br />
                  <Alert tone="critical" full>
                    Es necesario asignar un nombre al reporte.
                  </Alert>
                  <br />
                </Fragment>
              )}
              <br />

              <Button full onClick={onSubmit}>
                {t(`custom_report_dialog.${step}.cta`)}
              </Button>
            </div>
          )}
          {step === 'loading' && (
            <div>
              <Text>{t(`custom_report_dialog.${step}.cta`)}</Text>
              <br></br>
              <Flex justify="center" align="center">
                <Spinner />
              </Flex>
            </div>
          )}
          {step === 'ready' && (
            <div className={styles.icono1}>
              <Icon className={styles.icono3} name="mail-send-line" size="xl" />
              <Text className={styles.icono2}>{t(`mobility_report_dialog.${step}.desc`)}</Text>
            </div>
          )}
        </Stack>
      )}
    </Dialog>
  )

  const reportSelectorDialog = (
    <Dialog
      isOpen={confirmed}
      onClose={onDialogClose}
      aria-label={t(`custom_report_dialog.${step}.title`)}
      className={styles.customReportDialog}
    >
      {loadStatus === 'loading' && (
        <Flex justify="center" align="center">
          <Spinner />
        </Flex>
      )}
      {loadStatus === 'success' && (
        <Stack>
          <div className={styles.icono1}>
            <Icon className={styles.icono} name="markup-line" size="xl" />
            <Heading className={styles.icono2}>{t(`custom_report_dialog.${step}.title`)}</Heading>
          </div>
          {step === 'confirm' && (
            <div>
              <DropDownMenu
                label={t(`custom_report_dialog.report_selector.cta`)}
                value={currentReport}
                onChange={handleReportChange}
                instructionLabel={t(`custom_report_dialog.report_selector.placeholder`)}
                options={
                  orgReports === undefined ? (
                    <Loading />
                  ) : (
                    orgReports.map((report: any, index) => (
                      <DropDownElement key={index} value={report.id} label={report.report_name} />
                    ))
                  )
                }
              />
              <br />
              <Heading size="widget">{t(`custom_report_dialog.${step}.cta`)}</Heading>
              <br></br>
              <TextInput
                name="reportName"
                label={t(`custom_report_dialog.${step}.placeholder`)}
                value={reportName}
                onChange={handleInputChange}
              />

              {noReportName && (
                <Fragment>
                  <br />
                  <Alert tone="critical" full>
                    Es necesario asignar un nombre al reporte.
                  </Alert>
                  <br />
                </Fragment>
              )}
              <br />

              <Button full onClick={onSubmit}>
                {t(`custom_report_dialog.${step}.cta`)}
              </Button>
            </div>
          )}
          {step === 'loading' && (
            <div>
              <Text>{t(`custom_report_dialog.${step}.cta`)}</Text>
              <br></br>
              <Flex justify="center" align="center">
                <Spinner />
              </Flex>
            </div>
          )}
          {step === 'ready' && (
            <div className={styles.icono1}>
              <Icon className={styles.icono3} name="mail-send-line" size="xl" />
              <Text className={styles.icono2}>{t(`mobility_report_dialog.${step}.desc`)}</Text>
            </div>
          )}
        </Stack>
      )}
    </Dialog>
  )

  if (confirmed) {
    return organization?.customReport2 ? reportSelectorDialog : customReportDialog
  }
  return <Confirmation onConfirm={() => mapEditorDispatch.setConfirm(true)} />
}
CustomReportWizard.displayName = 'CustomReportWizard'
